import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectProps,
  Theme,
  useTheme,
} from "@mui/material";
import React from "react";

function getStyles(name: string, personName: readonly string[], theme: Theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface AdvancedOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string[];
  id: string;
  options?: string[];
  advancedOptions?: AdvancedOption[];
  label: string;
  labelId: string;
  onChange: SelectProps<string[]>["onChange"];
  error?: boolean;
  helperText?: React.ReactNode;
}

const ChipLightSelect: React.FC<CustomSelectProps & SelectProps<string[]>> = ({
  value,
  id,
  options,
  advancedOptions,
  label,
  labelId,
  onChange,
  error,
  helperText,
  ...props
}) => {
  const theme = useTheme();

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 300,
      },
    },
  };

  const getLabelFromValue = (itemValue: string) => {
    const option = advancedOptions?.find((item) => item.value === itemValue);
    return option?.label || itemValue;
  };

  const renderMenuItem = (option: string | AdvancedOption) => {
    if (typeof option === "string") {
      return (
        <MenuItem
          key={option}
          value={option}
          style={getStyles(option, value, theme)}
        >
          {option}
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={option.value}
        value={option.value}
        style={getStyles(option.value, value, theme)}
      >
        {option.label}
      </MenuItem>
    );
  };

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        {...props}
        id={id}
        labelId={labelId}
        label={label}
        multiple
        value={value}
        onChange={onChange as SelectProps<string[]>["onChange"]}
        input={<OutlinedInput id={`${id}-outlined`} label={label} />}
        MenuProps={menuProps}
        sx={{
          borderRadius: "14px",
          "& .MuiSelect-select": {
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: theme.palette.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
          },
        }}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(selected as string[]).map((selectedValue) => (
              <Chip
                key={selectedValue}
                label={getLabelFromValue(selectedValue)}
              />
            ))}
          </Box>
        )}
      >
        {(options || advancedOptions)?.map((option) => renderMenuItem(option))}
      </Select>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};

export default ChipLightSelect;
